/**
 * RequestCard Component
 * Displays a product access request with product info, status, and actions
 * Used in Affiliate Requests page (both creator and brand views)
 */

import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useDarkMode } from '../../hooks/useDarkMode';
import CommissionBadge from './CommissionBadge';

export interface RequestCardProps {
  requestId: string;
  productName: string;
  productImage: string;
  productPrice: number;
  commissionRate: number;
  commissionType: 'percentage' | 'fixed';
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requestedAt: string;
  creatorNickName?: string;
  creatorName?: string;
  creatorImage?: string;
  brandNickName?: string;
  brandName?: string;
  brandImage?: string;
  creatorMessage?: string;
  brandResponse?: string;
  creatorSocialLinks?: {
    instagram?: string;
    tiktok?: string;
    youtube?: string;
    website?: string;
  };
  viewType: 'creator' | 'brand'; // Determines which info to show
  // Affiliate settings
  affiliateStartDate?: string;
  affiliateEndDate?: string;
  affiliateMaxUses?: number;
  affiliateCurrentUses?: number;
  onApprove?: (requestId: string) => void;
  onReject?: (requestId: string) => void;
  onCancel?: (requestId: string) => void;
  onClick?: (requestId: string) => void;
}

export default function RequestCard({
  requestId,
  productName,
  productImage,
  productPrice,
  commissionRate,
  commissionType,
  status,
  requestedAt,
  creatorNickName,
  creatorName,
  creatorImage,
  brandNickName,
  brandName,
  brandImage,
  creatorMessage,
  brandResponse,
  creatorSocialLinks,
  viewType,
  affiliateStartDate,
  affiliateEndDate,
  affiliateMaxUses,
  affiliateCurrentUses,
  onApprove,
  onReject,
  onCancel,
  onClick,
}: RequestCardProps) {
  const isDarkMode = useDarkMode();

  const handleCardClick = () => {
    if (onClick) {
      onClick(requestId);
    }
  };

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onApprove) {
      onApprove(requestId);
    }
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onReject) {
      onReject(requestId);
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCancel) {
      onCancel(requestId);
    }
  };

  const getStatusChip = () => {
    const statusConfig = {
      pending: { label: '대기중', color: 'warning' as const, icon: <AccessTimeIcon /> },
      approved: { label: '승인됨', color: 'success' as const, icon: <CheckCircleIcon /> },
      rejected: { label: '거절됨', color: 'error' as const, icon: <CancelIcon /> },
      cancelled: { label: '취소됨', color: 'default' as const, icon: <CancelIcon /> },
    };

    const config = statusConfig[status];
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        bgcolor: isDarkMode ? '#1e1e1e' : '#fff',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        } : {},
      }}
      onClick={handleCardClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {/* Product Image */}
          <Box
            component="img"
            src={productImage || '/placeholder-product.png'}
            alt={productName}
            sx={{
              width: 100,
              height: 100,
              borderRadius: 2,
              objectFit: 'cover',
            }}
          />

          {/* Product Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: isDarkMode ? '#fff' : '#000',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {productName}
              </Typography>
              {getStatusChip()}
            </Box>

            <Typography variant="h6" sx={{ color: isDarkMode ? '#fff' : '#000', fontWeight: 'bold', mb: 1 }}>
              ₩{productPrice.toLocaleString()}
            </Typography>

            <CommissionBadge rate={commissionRate} type={commissionType} />
          </Box>
        </Box>

        {/* Creator/Brand Info - Clickable to shop page */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            cursor: 'pointer',
            p: 1,
            borderRadius: 1,
            transition: 'background-color 0.2s',
            '&:hover': {
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            // Get the seller_id from brand or creator based on viewType
            // This will be passed as a prop
            const storeUrl = viewType === 'creator'
              ? `https://clozet.my/shop/${brandName}`
              : `https://clozet.my/shop/${creatorName}`;
            window.open(storeUrl, '_blank');
          }}
        >
          <Avatar
            src={viewType === 'creator' ? brandImage : creatorImage}
            sx={{ width: 32, height: 32 }}
          >
            {viewType === 'creator' ? brandName?.charAt(0) : creatorName?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
              {viewType === 'creator' ? (brandName ? '브랜드' : '') : (creatorName ? '크리에이터' : '')}
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? '#fff' : '#000', fontWeight: 500 }}>
              {viewType === 'creator'
                ? `${brandNickName || '닉네임 없음'} / ${brandName || '브랜드명 없음'}`
                : `${creatorNickName || '닉네임 없음'} / ${creatorName || '크리에이터명 없음'}`
              }
            </Typography>
          </Box>
        </Box>

        {/* Creator Social Links (brand view only) */}
        {viewType === 'brand' && creatorSocialLinks && (
          Object.values(creatorSocialLinks).some(Boolean)
        ) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666', mr: 0.5 }}>
              크리에이터 채널:
            </Typography>
            {creatorSocialLinks.instagram && (
              <Tooltip title="Instagram">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); window.open(creatorSocialLinks.instagram, '_blank'); }}
                  sx={{ color: '#E1306C' }}
                >
                  <InstagramIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {creatorSocialLinks.tiktok && (
              <Tooltip title="TikTok">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); window.open(creatorSocialLinks.tiktok, '_blank'); }}
                  sx={{ color: isDarkMode ? '#fff' : '#000' }}
                >
                  <LanguageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {creatorSocialLinks.youtube && (
              <Tooltip title="YouTube">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); window.open(creatorSocialLinks.youtube, '_blank'); }}
                  sx={{ color: '#FF0000' }}
                >
                  <YouTubeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {creatorSocialLinks.website && (
              <Tooltip title="웹사이트">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); window.open(creatorSocialLinks.website, '_blank'); }}
                  sx={{ color: isDarkMode ? '#aaa' : '#555' }}
                >
                  <LanguageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Messages */}
        {creatorMessage && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
              크리에이터 메시지:
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? '#fff' : '#000' }}>
              {creatorMessage}
            </Typography>
          </Box>
        )}

        {brandResponse && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
              브랜드 응답:
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? '#fff' : '#000' }}>
              {brandResponse}
            </Typography>
          </Box>
        )}

        {/* Affiliate Settings Info */}
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 1.5,
          p: 1.5,
          borderRadius: 1,
          bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        }}>
          <Box sx={{ minWidth: 120 }}>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666', display: 'block' }}>
              게시 기간
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? '#fff' : '#000', fontWeight: 500 }}>
              {affiliateStartDate || affiliateEndDate
                ? `${affiliateStartDate ? formatDate(affiliateStartDate) : '-'} ~ ${affiliateEndDate ? formatDate(affiliateEndDate) : '-'}`
                : '-'
              }
            </Typography>
          </Box>
          <Box sx={{ minWidth: 80 }}>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666', display: 'block' }}>
              사용 횟수
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? '#fff' : '#000', fontWeight: 500 }}>
              {affiliateMaxUses !== undefined && affiliateMaxUses !== null
                ? `${affiliateCurrentUses ?? 0} / ${affiliateMaxUses}`
                : '-'
              }
            </Typography>
          </Box>
        </Box>

        {/* Request Date */}
        <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
          요청일: {formatDate(requestedAt)}
        </Typography>
      </CardContent>

      {/* Actions */}
      {status === 'pending' && (
        <CardActions sx={{ pt: 0, px: 2, pb: 2, gap: 1 }}>
          {viewType === 'brand' ? (
            <>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={handleApprove}
                startIcon={<CheckCircleIcon />}
                sx={{ flex: 1 }}
              >
                승인
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleReject}
                startIcon={<CancelIcon />}
                sx={{ flex: 1 }}
              >
                거절
              </Button>
            </>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={handleCancel}
              fullWidth
              startIcon={<CancelIcon />}
            >
              요청 취소
            </Button>
          )}
        </CardActions>
      )}
    </Card>
  );
}
